import { useState, useEffect } from 'react';
import { 
  ArrowLeft, Copy, Share2, Users, DollarSign, Gift, Check, Sparkles, 
  TrendingUp, Award, MessageCircle, Facebook, Twitter, Instagram, 
  Linkedin, Mail, RefreshCw, QrCode, Wallet, Download, Globe, CheckCircle2, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { copyToClipboard } from '../utils/clipboard';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchReferralData, invalidateCache } from '../store/slices/referralSlice';
import { logger } from '../utils/logger';
import { useAuthContext } from '../contexts/AuthContext';

interface ReferEarnLoggedInProps {
  onBack: () => void;
}

interface Referral {
  id: string;
  name: string;
  status: 'pending' | 'signed-up' | 'premium' | 'married';
  joinedDate: string;
  earned: number;
  avatar?: string;
}

interface Reward {
  id: string;
  title: string;
  description: string;
  points: number;
  icon: any;
  color: string;
  unlocked: boolean;
}

export default function ReferEarnLoggedIn({ onBack }: ReferEarnLoggedInProps) {
  const dispatch = useAppDispatch();
  const { user } = useAuthContext();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  
  // Get data from Redux store
  const {
    referralCode,
    referralLink,
    settings,
    rewards: rewardsList,
    referrals: myReferralsData,
    earnings,
    statistics,
    loading,
    error,
    cacheValid
  } = useAppSelector(state => state.referral);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchReferralData(false));
  }, [dispatch]);

  // Debug: Log the referral code state
  useEffect(() => {
    logger.log('🎯 ReferEarnLoggedIn - Referral Code:', referralCode);
    logger.log('🎯 ReferEarnLoggedIn - Referral Link:', referralLink);
    logger.log('🎯 ReferEarnLoggedIn - Loading:', loading);
    logger.log('🎯 ReferEarnLoggedIn - Error:', error);
  }, [referralCode, referralLink, loading, error]);

  const handleRefresh = async () => {
    setRefreshing(true);
    dispatch(invalidateCache());
    await dispatch(fetchReferralData(true));
    setRefreshing(false);
    toast.success('Referral data refreshed!');
  };

  // Transform referrals data for display
  const myReferrals: Referral[] = myReferralsData.map((ref: any) => ({
    id: ref._id,
    name: `${ref.firstName || ''} ${ref.lastName || ''}`.trim() || 'User',
    status: ref.isPremium ? 'premium' : 'signed-up',
    joinedDate: new Date(ref.createdAt).toLocaleDateString(),
    earned: 0,
    avatar: ref.profilePhoto
  }));

  // Map icon names to actual icons
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, any> = {
      'star': Sparkles,
      'zap': Check,
      'award': Award,
      'wallet': DollarSign,
      'crown': Sparkles,
      'gift': Gift,
      'dollar-sign': DollarSign
    };
    return iconMap[iconName?.toLowerCase()] || Gift;
  };

  // Map color names to Tailwind classes
  const getColorClass = (colorName: string) => {
    const colorMap: Record<string, string> = {
      'blue': 'bg-blue-500',
      'yellow': 'bg-yellow-500',
      'purple': 'bg-purple-500',
      'green': 'bg-green-500',
      'indigo': 'bg-indigo-500',
      'rose': 'bg-rose-500',
      'pink': 'bg-pink-500',
      'red': 'bg-red-500',
      'orange': 'bg-orange-500'
    };
    return colorMap[colorName?.toLowerCase()] || 'bg-purple-500';
  };

  // Transform rewards data for display from backend
  const rewards: Reward[] = rewardsList.map((reward: any, index: number) => ({
    id: reward._id || String(index),
    title: reward.title,
    description: reward.description,
    points: reward.rewardAmount || 500,
    icon: getIconComponent(reward.icon),
    color: getColorClass(reward.color),
    unlocked: true
  }));



  const handleCopyCode = () => {
    copyToClipboard(referralCode);
    toast.success('Referral code copied to clipboard!');
  };

  const handleCopyLink = () => {
    copyToClipboard(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleShare = (platform: string) => {
    const message = encodeURIComponent(`Join me on Atootbandan and find your perfect life partner! Use my code ${referralCode} to get started. ${referralLink}`);
    
    const urls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      twitter: `https://twitter.com/intent/tweet?text=${message}`,
      whatsapp: `https://wa.me/?text=${message}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${message}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
      email: `mailto:?subject=Join Atootbandan&body=${message}`
    };

    if (platform === 'instagram') {
      // Instagram doesn't support web sharing, so copy to clipboard
      copyToClipboard(`${referralCode}\n\n${referralLink}\n\nJoin me on Atootbandan and find your perfect life partner!`);
      toast.success('Referral code and link copied! You can now paste it in Instagram.');
      return;
    }

    if (urls[platform]) {
      window.open(urls[platform], '_blank');
      toast.success(`Opening ${platform}...`);
    }
  };

  const handleRedeemReward = (reward: Reward) => {
    if (statistics.referralPoints >= reward.points) {
      toast.success(`${reward.title} redeemed successfully!`);
    } else {
      toast.error(`You need ${reward.points - statistics.referralPoints} more points`);
    }
  };

  const handleApplyAffiliate = async () => {
    try {
      const apiClient = (await import('../lib/api-client')).default;
      const response = await apiClient.post('/users/request-affiliate');
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reload user data to reflect the change
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit request');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
      'signed-up': { label: 'Signed Up', className: 'bg-blue-100 text-blue-700' },
      premium: { label: 'Premium', className: 'bg-purple-100 text-purple-700' },
      married: { label: 'Married ❤️', className: 'bg-pink-100 text-pink-700' }
    };
    return badges[status] || badges.pending;
  };

  const totalEarnings = statistics.totalEarned + statistics.pendingEarnings;

  if (loading && !cacheValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referral data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-rose-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-6 shadow-lg sticky top-0 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold">Refer & Earn</h1>
                <p className="text-sm text-white/90">Invite friends, earn when they upgrade!</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                <div>
                  <p className="text-xs text-white/80">Earned</p>
                  <p className="font-bold">₹{statistics.totalEarned.toFixed(2)}</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="text-xs">Total Referrals</CardDescription>
              <CardTitle className="text-2xl">{statistics.totalReferrals}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-xs text-green-600">
                <TrendingUp className="h-3 w-3" />
                <span>Active referrals</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="text-xs">Total Earned</CardDescription>
              <CardTitle className="text-2xl">₹{statistics.totalEarned.toLocaleString()}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-600">Pending: ₹{statistics.pendingEarnings.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="text-xs">Premium Conversions</CardDescription>
              <CardTitle className="text-2xl">{statistics.premiumConversions}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-600">
                {statistics.totalReferrals > 0 
                  ? `${Math.round((statistics.premiumConversions / statistics.totalReferrals) * 100)}% conversion` 
                  : '0% conversion'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="text-xs">Redeemable</CardDescription>
              <CardTitle className="text-2xl">₹{statistics.redeemable.toLocaleString()}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button size="sm" className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600">
                Withdraw
              </Button>
            </CardContent>
          </Card>
        </div>



        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="referrals">My Referrals</TabsTrigger>
            <TabsTrigger value="rewards">Rewards</TabsTrigger>
            <TabsTrigger value="affiliate">Affiliate</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* My Referral Code Card - Featured */}
            <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">My Referral Code</CardTitle>
                    <CardDescription>Share this code to earn rewards</CardDescription>
                  </div>
                  <div className="bg-pink-100 p-3 rounded-full">
                    <Gift className="h-6 w-6 text-pink-600" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {referralCode ? (
                  <>
                    <div className="bg-white p-6 rounded-lg border-2 border-pink-300 text-center">
                      <p className="text-sm text-gray-600 mb-2">Your Unique Code</p>
                      <p className="text-3xl font-bold text-pink-600 tracking-wider mb-4">
                        {referralCode}
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button 
                          onClick={handleCopyCode} 
                          className="flex-1 max-w-xs bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Code
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Referral Link</p>
                      <div className="flex gap-2">
                        <Input
                          value={referralLink}
                          readOnly
                          className="text-sm"
                        />
                        <Button onClick={handleCopyLink} variant="outline">
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-300">
                    <RefreshCw className={`h-12 w-12 mx-auto text-pink-500 mb-3 ${loading ? 'animate-spin' : ''}`} />
                    <p className="text-gray-900 font-medium mb-2">
                      {loading ? 'Generating your referral code...' : 'Loading referral code...'}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      Your unique code will appear here
                    </p>
                    {!loading && (
                      <Button 
                        onClick={handleRefresh}
                        className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Share Options */}
            <Card>
              <CardHeader>
                <CardTitle>Share on Social Media</CardTitle>
                <CardDescription>Spread the word and earn rewards</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[
                    { id: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', color: 'bg-green-500' },
                    { id: 'facebook', icon: Facebook, label: 'Facebook', color: 'bg-blue-600' },
                    { id: 'twitter', icon: Twitter, label: 'Twitter', color: 'bg-blue-400' },
                    { id: 'instagram', icon: Instagram, label: 'Instagram', color: 'bg-pink-500' },
                    { id: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: 'bg-blue-700' },
                    { id: 'email', icon: Mail, label: 'Email', color: 'bg-gray-600' }
                  ].map(platform => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.id}
                        onClick={() => handleShare(platform.id)}
                        disabled={!referralCode}
                        className="flex flex-col items-center gap-2 p-4 rounded-lg border hover:shadow-md transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className={`${platform.color} p-3 rounded-full text-white`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="text-xs font-medium">{platform.label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Apply as Affiliate Banner */}
            {!user?.isAffiliateEnabled && (
              <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">Become an Affiliate Partner</CardTitle>
                      <CardDescription>
                        Earn higher commissions by promoting our platform on your website
                      </CardDescription>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-full">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {user?.affiliateRequestedAt ? (
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <RefreshCw className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-900">Request Pending</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Your affiliate request is under review. Our team will contact you soon!
                          </p>
                          <p className="text-xs text-yellow-600 mt-2">
                            Requested on {new Date(user.affiliateRequestedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-white rounded-lg border border-purple-100">
                          <DollarSign className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                          <p className="font-medium text-gray-900">Higher Earnings</p>
                          <p className="text-sm text-gray-600">Better commission rates</p>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg border border-purple-100">
                          <Globe className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                          <p className="font-medium text-gray-900">WordPress Plugin</p>
                          <p className="text-sm text-gray-600">Easy website integration</p>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg border border-purple-100">
                          <Award className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                          <p className="font-medium text-gray-900">Priority Support</p>
                          <p className="text-sm text-gray-600">Dedicated assistance</p>
                        </div>
                      </div>
                      <Button 
                        onClick={handleApplyAffiliate}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                        size="lg"
                      >
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        Apply as Affiliate
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* How It Works */}
            <Card className="border-2 border-rose-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-rose-600" />
                  How It Works
                </CardTitle>
                <CardDescription>Simple 3-step process to start earning</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {[
                    {
                      step: '1',
                      title: 'Share Your Unique Code',
                      description: 'Send your referral code to friends & family who are looking for a life partner',
                      icon: Share2,
                      color: 'bg-blue-500'
                    },
                    {
                      step: '2',
                      title: 'They Sign Up & Use Your Code',
                      description: 'Your friend creates an account and enters your referral code when purchasing a premium plan',
                      icon: Users,
                      color: 'bg-green-500'
                    },
                    {
                      step: '3',
                      title: 'You Get Paid!',
                      description: settings?.referralType === 'percentage' 
                        ? `Earn ${settings?.referralPercentage || 10}% commission when they purchase any premium plan`
                        : `Earn ₹${settings?.referralAmount || 100} when they purchase any premium plan`,
                      icon: Gift,
                      color: 'bg-purple-500'
                    }
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`${item.color} p-3 rounded-full text-white`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          {i < 2 && (
                            <div className="w-0.5 h-12 bg-gray-200 mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-gray-500">STEP {item.step}</span>
                          </div>
                          <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Important Note */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex gap-2">
                    <DollarSign className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900 mb-1">Important Note:</p>
                      <p className="text-sm text-amber-800">
                        Earnings are credited <span className="font-bold">only when your referral purchases a premium plan</span>. 
                        Simply signing up does not generate earnings.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Earning Structure */}
            <Card>
              <CardHeader>
                <CardTitle>Earning Structure</CardTitle>
                <CardDescription>Earn more when your referrals upgrade</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {settings?.earningStructureMessages && settings.earningStructureMessages.length > 0 ? (
                    settings.earningStructureMessages.map((item: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <div>
                          <span className="text-sm font-medium">{item.title}</span>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          ₹{item.amount}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <span className="text-sm">Friend buys Premium plan</span>
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          {settings?.referralType === 'percentage' 
                            ? `${settings?.referralPercentage || 10}% commission` 
                            : `₹${settings?.referralAmount || 500}`}
                        </Badge>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Referrals Tab */}
          <TabsContent value="referrals" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>My Referrals ({myReferrals.length})</CardTitle>
                <CardDescription>Track all your referrals and earnings</CardDescription>
              </CardHeader>
              <CardContent>
                {myReferrals.length > 0 ? (
                  <div className="space-y-3">
                    {myReferrals.map(referral => {
                      const statusBadge = getStatusBadge(referral.status);
                      return (
                        <div key={referral.id} className="flex items-center justify-between p-4 rounded-lg border hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={referral.avatar} />
                              <AvatarFallback>{referral.name[0]}</AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-medium">{referral.name}</h4>
                              <p className="text-xs text-gray-500">Joined: {referral.joinedDate}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={statusBadge.className}>
                              {statusBadge.label}
                            </Badge>
                            <p className="text-sm font-semibold mt-1 text-pink-600">
                              ₹{referral.earned}
                            </p>
                            {referral.earned === 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                You'll earn when they buy a plan
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <h3 className="font-medium text-gray-900 mb-1">No referrals yet</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Start sharing your referral code to earn rewards!
                    </p>
                    <Button 
                      onClick={() => setActiveTab('overview')}
                      className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                    >
                      Share Your Code
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rewards.map(reward => {
                const Icon = reward.icon;
                const canRedeem = statistics.referralPoints >= reward.points;
                return (
                  <Card key={reward.id} className={!reward.unlocked ? 'opacity-60' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`${reward.color} p-3 rounded-lg text-white`}>
                            <Icon className="h-6 w-6" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{reward.title}</CardTitle>
                            <CardDescription className="text-xs">
                              {reward.description}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Required Points</p>
                          <p className="text-xl font-semibold">{reward.points}</p>
                        </div>
                        <Button
                          onClick={() => handleRedeemReward(reward)}
                          disabled={!canRedeem || !reward.unlocked}
                          className={canRedeem && reward.unlocked ? 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600' : ''}
                        >
                          {!reward.unlocked ? 'Locked' : canRedeem ? 'Redeem' : 'Not Enough'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Affiliate Tab */}
          <TabsContent value="affiliate" className="space-y-6">
            {user?.isAffiliateEnabled ? (
              <>
                {/* Affiliate Dashboard */}
                <Card className="border-2 border-purple-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      Affiliate Program
                    </CardTitle>
                    <CardDescription>
                      Download WordPress plugin and earn commissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Affiliate ID Display */}
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-600 mb-2">Your Affiliate ID</p>
                      <code className="text-2xl font-bold text-purple-600">
                        {referralCode}
                      </code>
                    </div>

                    {/* WordPress Plugin Info */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        WordPress Plugin Available
                      </h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Download our official WordPress plugin from WordPress.org and use your affiliate ID to start earning commissions from your website traffic.
                      </p>
                      <Button
                        onClick={() => window.open('https://wordpress.org', '_blank')}
                        variant="outline"
                        className="w-full border-blue-300"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Visit WordPress.org
                      </Button>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-600">Total Clicks</p>
                        <p className="text-2xl font-bold">0</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-600">Commission Earned</p>
                        <p className="text-2xl font-bold">₹0</p>
                      </div>
                    </div>

                    {/* How It Works */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        How It Works
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Download and install plugin on your WordPress site</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Enter your affiliate ID in plugin settings</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Choose where to display ads (Header, Sidebar, Post Bottom)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Visitors click ads and register → You earn commission!</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Not Enabled Message */}
                <Card className="border-2 border-yellow-200 bg-yellow-50">
                  <CardContent className="p-12 text-center">
                    <div className="bg-yellow-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="h-10 w-10 text-yellow-600" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Affiliate Program</h3>
                    <p className="text-gray-700 mb-2 text-lg">
                      You need to be approved as an affiliate to access this feature.
                    </p>
                    <p className="text-gray-600 mb-6">
                      Contact admin to enable affiliate access for your account.
                    </p>
                    <div className="p-4 bg-white rounded-lg border border-yellow-300 max-w-md mx-auto">
                      <p className="text-sm text-gray-600">
                        <strong>Benefits when enabled:</strong>
                      </p>
                      <ul className="text-sm text-left mt-2 space-y-1">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Download WordPress plugin
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Earn commissions from referrals
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Track clicks and earnings
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}